import { createRoute, redirect, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Route as RootRoute } from "./__root";
import { useAdminApi } from "../providers/api-context";
import { Button } from "@carry/ui";
import { notificationTypeLabel } from "@carry/contract";

export const Route = createRoute({
  getParentRoute: () => RootRoute,
  path: "/notifications",
  beforeLoad: ({ context }) => {
    if (context.authLoading) return;
    if (!context.session) throw redirect({ to: "/auth/login" });
  },
  component: NotificationsPage,
});

const TYPE_INK: Record<string, string> = {
  distance_deviation_alert: "var(--warn)",
  alcohol_check_failed: "var(--danger)",
  vehicle_inspection_ng: "var(--danger)",
  daily_report_rejected: "var(--warn)",
  license_expiration_alert: "var(--warn)",
};

function formatRelative(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "たった今";
  if (mins < 60) return `${mins}分前`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}時間前`;
  return `${Math.floor(hrs / 24)}日前`;
}

export function NotificationsPage() {
  const api = useAdminApi();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["notifications"],
    queryFn: () => api.notification.list({ limit: 100 }),
    refetchInterval: 30_000,
  });

  const markReadMutation = useMutation({
    mutationFn: (publicId: string) => api.notification.markRead({ publicId }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["notifications"] }),
  });

  const markAllMutation = useMutation({
    mutationFn: () => api.notification.markAllRead(),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["notifications"] }),
  });

  const notifications = data?.data ?? [];
  const unreadCount = data?.unread_count ?? 0;

  return (
    <div style={{ margin: -24 }}>
      <div
        style={{
          padding: "12px 32px",
          display: "flex",
          alignItems: "center",
          gap: 24,
          borderBottom: "0.5px solid var(--rule-softer)",
          background: "var(--bg)",
        }}
      >
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => markAllMutation.mutate()}
            disabled={markAllMutation.isPending || unreadCount === 0}
          >
            すべて既読にする
          </Button>
        </div>
      </div>

      <div style={{ padding: "24px 32px 32px" }}>
        {isLoading && <div style={{ fontSize: 12, color: "var(--ink-3)" }}>読み込み中…</div>}
        {!isLoading && notifications.length === 0 && (
          <div
            style={{
              fontSize: 13,
              color: "var(--ink-3)",
              textAlign: "center",
              padding: "48px 0",
            }}
          >
            通知はありません
          </div>
        )}
        {!isLoading && notifications.length > 0 && (
          <div
            style={{
              background: "var(--bg-raised)",
              border: "0.5px solid var(--rule)",
              borderRadius: 3,
            }}
          >
            {notifications.map((n, i) => {
              const target = n.target?.kind === "order" ? n.target : null;
              const content = (
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
                    <span
                      style={{
                        fontSize: 10,
                        padding: "1px 5px",
                        border: "0.5px solid var(--rule-soft)",
                        borderRadius: 2,
                        fontFamily: "var(--f-mono)",
                        color: TYPE_INK[n.type] ?? "var(--ink-3)",
                      }}
                    >
                      {notificationTypeLabel(n.type)}
                    </span>
                    <span style={{ fontSize: 11, color: "var(--ink-3)" }}>
                      {formatRelative(n.created_at)}
                    </span>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: n.read_at ? 400 : 500, marginBottom: 2 }}>
                    {n.title}
                  </div>
                  <div style={{ fontSize: 12, color: "var(--ink-2)" }}>{n.body}</div>
                  {target && (
                    <div style={{ fontSize: 11, color: "var(--accent)", marginTop: 6 }}>
                      対象注文: {target.order_number ?? target.public_id}
                    </div>
                  )}
                </div>
              );

              return (
                <div
                  key={n.id}
                  style={{
                    display: "flex",
                    gap: 14,
                    padding: "14px 18px",
                    borderBottom:
                      i < notifications.length - 1 ? "0.5px solid var(--rule-soft)" : "none",
                    background: n.read_at ? "transparent" : "var(--accent-wash)",
                    alignItems: "flex-start",
                  }}
                >
                  <div
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      background: n.read_at
                        ? "var(--rule-soft)"
                        : (TYPE_INK[n.type] ?? "var(--accent)"),
                      flexShrink: 0,
                      marginTop: 5,
                    }}
                  />
                  {target ? (
                    <Link
                      to="/orders/$publicId"
                      params={{ publicId: target.public_id }}
                      aria-label={`対象注文: ${target.order_number ?? target.public_id} の詳細を開く`}
                      onClick={() => {
                        if (!n.read_at) markReadMutation.mutate(n.id);
                      }}
                      style={{
                        flex: 1,
                        minWidth: 0,
                        color: "inherit",
                        textDecoration: "none",
                      }}
                    >
                      {content}
                    </Link>
                  ) : (
                    <div style={{ flex: 1, minWidth: 0 }}>{content}</div>
                  )}
                  {!n.read_at && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      style={{ fontSize: 11, flexShrink: 0 }}
                      onClick={() => markReadMutation.mutate(n.id)}
                      disabled={markReadMutation.isPending}
                    >
                      既読
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
